import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { getRecentCampaigns } from '../api';
import { PaperAirplaneIcon } from './Icons';
import { DashboardCard } from './PremiumDesign';
import { V2Table } from '@/common/components';
import { useFormatters } from '../hooks/useFormatters';
import { parseApiError } from '@/common/utils';
import { StatusBadge, SkeletonText } from '@/components';
import arrowRight from '../assets/arrow-right.png';

const CAMPAIGN_STATUS = {
  completed: { tone: 'success', label: 'Completed' },
  processing: { tone: 'warning', label: 'In Progress' },
  pending: { tone: 'info', label: 'Pending' },
  scheduled: { tone: 'info', label: 'Scheduled' },
  interval: { tone: 'warning', label: 'Interval' },
  paused: { tone: 'warning', label: 'Paused' },
  stopped: { tone: 'neutral', label: 'Stopped' },
  failed: { tone: 'error', label: 'Failed' },
};

const encodeId = (id) => {
  if (!id) return '';
  try {
    return btoa(String(id));
  } catch {
    return String(id);
  }
};

const normalizeCampaign = (campaign) => {
  const id = campaign.id ?? campaign.campaign_id;

  return {
    id,
    name: campaign.campaign_name || `Campaign #${id}`,
    templateName: campaign.template_name || '-',
    totalCount:
      campaign.total_consent ??
      campaign.total_count ??
      campaign.total_contacts ??
      0,
    sentCount: campaign.sent_count ?? campaign.sent ?? 0,
    failedCount: campaign.failed_count ?? campaign.failed ?? 0,
    skippedCount: campaign.skipped_count ?? campaign.skipped ?? 0,
    status: campaign.status || 'pending',
    language: campaign.language || campaign.template_language || '-',
    createdAt: campaign.created_at || campaign.created_date,
  };
};

import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';

export default function RecentCampaigns({ companyId }) {
  const navigate = useNavigate();
  const { formatDate, formatNumber } = useFormatters();

  const {
    data: campaignData,
    isLoading: isInitialLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ['recent-campaigns', companyId],
    queryFn: () => getRecentCampaigns(companyId),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && campaignData !== undefined,
    300,
    500,
  );
  const isLoading = isInitialLoading || showDelayedSkeleton;

  useEffect(() => {
    if (isError) {
      const { message } = parseApiError(error);
      toast.error(message || 'Failed to load campaigns');
    }
  }, [isError, error]);

  const campaignResults = Array.isArray(campaignData)
    ? campaignData
    : campaignData?.results ||
      campaignData?.campaigns ||
      campaignData?.data ||
      [];
  const campaignsList = campaignResults.map(normalizeCampaign);

  const formatCampDate = (dateStr) => {
    return formatDate(dateStr, {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const openCampaign = (camp) =>
    navigate(`/campaigns/${encodeId(camp.id)}`, {
      state: { fromDashboard: true },
    });

  const campaignColumns = [
    {
      id: 'name',
      label: 'Campaign Name',
      defaultWidth: 180,
      minWidth: 110,
      resizable: true,
      skeleton: <SkeletonText width="w-28" />,
      cell: (camp) => <span className="truncate block">{camp.name}</span>,
    },
    {
      id: 'template',
      label: 'Template Name',
      defaultWidth: 200,
      minWidth: 130,
      resizable: true,
      skeleton: <SkeletonText width="w-32" />,
      cell: (camp) => (
        <span className="truncate block">{camp.templateName}</span>
      ),
    },
    {
      id: 'total',
      label: 'Total Consent',
      defaultWidth: 120,
      minWidth: 80,
      resizable: true,
      align: 'center',
      skeleton: <SkeletonText width="w-8" className="mx-auto" />,
      cell: (camp) => formatNumber(camp.totalCount),
    },
    {
      id: 'sent',
      label: 'Sent',
      headerClassName: 'text-[#22C55E]',
      defaultWidth: 80,
      minWidth: 60,
      resizable: true,
      align: 'center',
      skeleton: <SkeletonText width="w-8" className="mx-auto" />,
      cell: (camp) => (
        <span className="text-primary font-medium">
          {formatNumber(camp.sentCount)}
        </span>
      ),
    },
    {
      id: 'failed',
      label: 'Failed',
      headerClassName: 'text-[#EF4444]',
      defaultWidth: 80,
      minWidth: 60,
      resizable: true,
      align: 'center',
      skeleton: <SkeletonText width="w-8" className="mx-auto" />,
      cell: (camp) => (
        <span className="text-status-error font-medium">
          {formatNumber(camp.failedCount)}
        </span>
      ),
    },
    {
      id: 'skipped',
      label: 'Skipped',
      headerClassName: 'text-[#F59E0B]',
      defaultWidth: 90,
      minWidth: 70,
      resizable: true,
      align: 'center',
      skeleton: <SkeletonText width="w-8" className="mx-auto" />,
      cell: (camp) => (
        <span className="text-status-warning font-medium">
          {formatNumber(camp.skippedCount)}
        </span>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      defaultWidth: 120,
      minWidth: 90,
      resizable: true,
      align: 'center',
      skeleton: (
        <SkeletonText
          width="w-20"
          height="h-6"
          className="rounded-full mx-auto"
        />
      ),
      cell: (camp) => (
        <StatusBadge tone={CAMPAIGN_STATUS[camp.status]?.tone || 'neutral'}>
          {CAMPAIGN_STATUS[camp.status]?.label || camp.status}
        </StatusBadge>
      ),
    },
    {
      id: 'language',
      label: 'Language',
      defaultWidth: 100,
      minWidth: 70,
      resizable: true,
      skeleton: <SkeletonText width="w-12" />,
      cell: (camp) => (
        <span className="uppercase truncate block">{camp.language}</span>
      ),
    },
    {
      id: 'created',
      label: 'Created Date',
      defaultWidth: 160,
      minWidth: 125,
      resizable: true,
      skeleton: <SkeletonText width="w-24" />,
      cell: (camp) => (
        <span className="truncate block">{formatCampDate(camp.createdAt)}</span>
      ),
    },
    {
      id: 'action',
      label: 'Action',
      defaultWidth: 150,
      minWidth: 120,
      resizable: false,
      align: 'center',
      stopPropagation: true,
      skeleton: <SkeletonText width="w-24" height="h-8" className="mx-auto" />,
      cell: (camp) => (
        <button
          onClick={() => openCampaign(camp)}
          className="inline-flex items-center justify-center w-auto h-7 px-2 gap-1 rounded-md text-xs font-medium text-primary hover:text-text-primary bg-primary/10 transition-all focus:outline-none"
        >
          View Campaign
          <img
            src={arrowRight}
            alt=""
            className="w-3.5 h-3.5 object-contain ml-1"
          />
        </button>
      ),
    },
  ];

  return (
    <DashboardCard className="recent-campaigns flex flex-col gap-2 !rounded-[12px] !p-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-text-primary sm:text-h4">
          Recent Campaigns
        </h2>
        <Link
          to="/campaigns"
          className="inline-flex items-center gap-1 text-h5 font-medium text-primary transition-colors"
        >
          View all
          <img
            src={arrowRight}
            alt=""
            className="w-4 h-4 object-contain text-primary/80"
          />
        </Link>
      </div>

      <V2Table
        bare
        compact
        columns={campaignColumns}
        data={isError ? [] : campaignsList}
        rowKey="id"
        storageKey="dashboard_recent_campaigns_column_widths"
        loading={isLoading || !campaignData}
        empty={
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="w-16 h-16 bg-input-background border border-border-default rounded-full flex items-center justify-center">
              <PaperAirplaneIcon className="w-8 h-8 opacity-45 text-text-secondary" />
            </div>
            <span className="text-base font-medium">
              No recent campaigns found
            </span>
          </div>
        }
        onRowClick={openCampaign}
      />
    </DashboardCard>
  );
}
